import axios, {AxiosRequestConfig, AxiosResponse} from 'axios';
import type {Request, Response} from 'express';
import {apiDebuggingContext} from '../../express/apiDebuggingContext';
import {HttpHeaderKey, MimeType} from '../../express/constants';
import {
  AUTH_HEADER_STOKEN,
  AUTH_HEADER_USER_ID,
  HEADER_DEVICE_ID,
  HEADER_DEVICE_LANG,
  HEADER_GW_WEB_KEY,
  HEADER_GW_WEB_TYPE,
  HEADER_GW_WEB_TYPE_VALUE_MOBILE,
  HEADER_GW_WEB_TYPE_VALUE_PC,
  HEADER_GW_WEB_VALUE,
  HEADER_SERVICE_LANG,
  HEADER_X_FORWARDED_FOR,
} from '../auth/constants';
import {getClientIp} from '../auth/getClientIp';
import {AuthToken} from '../auth/JwtToken.type';
import {apiHttpAgent, apiHttpsAgent} from '../bff/agent';
import {getUaFromServerSide} from '../bff/getUaFromServerSide';
import {getEnv} from '../common/env/env';
import {getApiProxyCookieString} from '../common/env/utils/getApiProxyCookieString';
import {getTranslatedApiMessage} from '../common/getTranslatedApiMessage';
import {requestContext} from '../common/requestContext';
import {COOKIE_KEY_DEVICE_ID} from '../common/shared/constants';
import {LogType} from '../log/constant';
import {logBff} from '../log/logger';

/**
 * 서버사이드 axios 클라이언트
 * 공통 Header 처리, 로깅 등이 되어있음
 */
const axiosInstance = axios.create({
  httpAgent: apiHttpAgent,
  httpsAgent: apiHttpsAgent,
});

axiosInstance.interceptors.request.use(
  config => {
    const debuggingRequest = {
      headers: config.headers,
      url: config.url || 'axios-url',
      body: config.data,
      method: config.method || 'unknown',
    };
    apiDebuggingContext.putIfDebugging({
      request: debuggingRequest,
    });
    logBff(LogType.AxiosRequest, debuggingRequest);
    return config;
  },
  error => {
    logBff(LogType.AxiosRequestError, error);
    return Promise.reject(error);
  }
);

axiosInstance.interceptors.response.use(
  response => {
    logBff(LogType.AxiosResponse, {
      status: response.status,
      body: response.data,
    });
    apiDebuggingContext.putIfDebugging({
      response: {
        headers: response.headers,
        url: response.config.url || 'axios-url',
        body: response.data,
        status: response.status,
      },
    });

    // billing API의 경우 응답이 object가 아니라 string인 경우가 있어서 예외처리 합니다.
    if (typeof response.data !== 'object') {
      return response;
    }

    return {
      ...response,
      data: {
        ...response.data,
        message: getTranslatedApiMessage(response.data),
        original_message:
          response.data.original_message ?? response.data.message,
      },
    };
  },
  error => {
    logBff(LogType.AxiosResponseError, error);
    return Promise.reject(error);
  }
);

/**
 * Gateway 요청을 편하기 해주는 헬퍼. 공통 로깅 처리 되어있음(현재 니콜라스만 확인됨)
 */
export async function axiosFetch({
  req,
  res,
  shouldAuth = false,
  authToken,
  url,
  config,
  excludeAuthHeaders = false,
}: {
  req?: Request;
  res?: Response;
  shouldAuth?: boolean;
  /**
   * 이미 존재하는 authToken 이 있으면 넣어줍니다. 없으면 쿠키의 jwt 토큰 통해 사용하지만 이 경우 복호화 비용이 발생합니다
   */
  authToken?: AuthToken;
  url: string;
  config?: AxiosRequestConfig | undefined;
  /**
   * true로 설정하면 X-Stoken, X-User-Id 헤더를 제외합니다. 로그인/회원가입 등 인증 전 API에 사용합니다.
   */
  excludeAuthHeaders?: boolean;
}): Promise<AxiosResponse<any>> {
  const cookies = req?.cookies || [];
  /** 가입 시점에는 카카오SDK에서 발급한 인증 정보만 담고 있습니다. */
  const deviceId = cookies[COOKIE_KEY_DEVICE_ID] || 'default_device_id';
  const userAgent = getUaFromServerSide({request: req, requestType: 'proxy'});

  let requestAuthToken = authToken;
  if (!requestAuthToken) {
    const jwt = res?.locals.jwt;
    requestAuthToken = jwt?.authToken;
    if (shouldAuth && !jwt) {
      const e = {status: 401};
      throw e;
    }
  }
  const allowedCookieString = getApiProxyCookieString(req?.headers?.cookie);
  let fetchConfig = config || {};
  fetchConfig = {
    timeout: 20000, // API 사용하는 쪽에서 별도 지정할 수 있음
    ...fetchConfig,
    headers: {
      [HEADER_GW_WEB_KEY]: HEADER_GW_WEB_VALUE,
      [HEADER_DEVICE_ID]: deviceId,
      [HEADER_GW_WEB_TYPE]: requestContext?.requestInfo?.display?.isMobile
        ? HEADER_GW_WEB_TYPE_VALUE_MOBILE
        : HEADER_GW_WEB_TYPE_VALUE_PC,
      [HEADER_X_FORWARDED_FOR]: getClientIp(req),
      [HttpHeaderKey.ContentType]: fetchConfig.headers?.[
        HttpHeaderKey.ContentType
      ]
        ? fetchConfig.headers?.[HttpHeaderKey.ContentType]
        : MimeType.FormUrlEncoded,
      [HttpHeaderKey.UserAgent]: userAgent,
      ...(allowedCookieString && {
        [HttpHeaderKey.Cookie]: allowedCookieString,
      }),
      ...(!excludeAuthHeaders &&
        requestAuthToken &&
        requestAuthToken.stoken &&
        requestAuthToken.userId && {
          [AUTH_HEADER_STOKEN]: requestAuthToken.stoken,
          [AUTH_HEADER_USER_ID]: requestAuthToken.userId,
        }),
      ...(requestContext.requestInfo?.lang?.deviceLang && {
        [HEADER_DEVICE_LANG]: requestContext.requestInfo.lang.deviceLang,
      }),
      ...(requestContext.requestInfo?.lang?.serviceLang && {
        [HEADER_SERVICE_LANG]: requestContext.requestInfo.lang.serviceLang,
      }),
      ...(fetchConfig.headers || {}),
    },
  };

  return axiosInstance(
    `${url.startsWith('http') ? '' : getEnv().gatewayOrigin}${url}`,
    fetchConfig
  );
}
